import { useCallback, useEffect, useState } from 'react';
import { fetchCalendarEvents } from '../utils/calendarService';

// Get calendar ID from environment variable
const CALENDAR_ID = process.env.REACT_APP_GOOGLE_CALENDAR_ID || '';

/**
 * Parse event data from Google Calendar
 * - Title = Location/Venue name (e.g., "Westin, Powai")
 * - Description = Show name (e.g., "Brunch Show")
 * - Location field = Google Maps hyperlink
 * 
 * @param {string} title - Event title (location/venue name)
 * @param {string} description - Event description (show name)
 * @param {string} calendarLocation - Location field from calendar (Google Maps URL)
 * @returns {Object} - { venue, mapsUrl, showName }
 */
const parsePerformanceEvent = (title, description, calendarLocation) => {
  // Clean up title - remove escape characters like backslashes
  const cleanTitle = title 
    ? title.replace(/\\/g, '').trim() 
    : '';
  
  // Clean up description - remove HTML tags, escape chars, and extra whitespace
  const cleanDescription = description 
    ? description
        .replace(/<[^>]*>/g, '')  // Remove HTML tags
        .replace(/\\n/g, ' ')      // Replace \n with space
        .replace(/\\/g, '')        // Remove backslashes
        .trim() 
    : '';
  
  // Extract Google Maps URL from location field
  let mapsUrl = '';
  if (calendarLocation) {
    const cleanLocation = calendarLocation.replace(/\\/g, '').trim();
    // Check if it's a URL
    if (cleanLocation.startsWith('http://') || cleanLocation.startsWith('https://')) {
      mapsUrl = cleanLocation;
    } else if (cleanLocation.includes('google.com/maps') || cleanLocation.includes('goo.gl/maps') || cleanLocation.includes('maps.app.goo.gl')) {
      mapsUrl = cleanLocation;
    } else if (cleanLocation) {
      // If it's just an address, create a Google Maps search URL
      mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cleanLocation)}`;
    }
  }
  
  return {
    venue: cleanTitle || 'TBA',
    mapsUrl,
    showName: cleanDescription || 'Live Performance',
  };
};

/**
 * Get the start and end of the current month
 * @returns {Object} - { start: Date, end: Date }
 */
const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  return { start, end };
};

/**
 * Get the start and end of next month
 * @returns {Object} - { start: Date, end: Date }
 */
const getNextMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59);
  return { start, end };
};

/**
 * Hook to fetch performance events from Google Calendar
 * Filters for events starting with "@" in the title
 * @param {boolean} includeNextMonth - Whether to include next month's events
 * @returns {Object} - { events, isLoading, error, refetch, currentMonth, nextMonth }
 */
const usePerformanceEvents = (includeNextMonth = true) => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPerformances = useCallback(async () => {
    if (!CALENDAR_ID) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch events from calendar
      const allEvents = await fetchCalendarEvents(CALENDAR_ID);
      
      const currentMonthRange = getCurrentMonthRange();
      const nextMonthRange = getNextMonthRange();
      
      // Filter for upcoming events and process them
      const performanceEvents = allEvents
        .filter(event => {
          if (!event.title || !event.start) return false;
          
          // Only include upcoming events (today onwards)
          const eventDate = new Date(event.start);
          eventDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          return eventDate >= today;
        })
        .map(event => {
          const parsed = parsePerformanceEvent(event.title, event.description, event.location);
          return {
            ...event,
            ...parsed,
            formattedDate: formatEventDate(event.start),
            formattedTime: formatEventTime(event.start),
            dayOfWeek: getDayOfWeek(event.start),
            dayOfMonth: event.start.getDate(),
            month: getMonthName(event.start),
            isThisMonth: event.start >= currentMonthRange.start && event.start <= currentMonthRange.end,
            isNextMonth: event.start >= nextMonthRange.start && event.start <= nextMonthRange.end,
          };
        })
        .sort((a, b) => a.start - b.start);

      setEvents(performanceEvents);
    } catch (err) {
      setError(err.message || 'Failed to fetch performances');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPerformances();
  }, [fetchPerformances]);

  // Get current month events
  const currentMonthEvents = events.filter(e => e.isThisMonth);
  
  // Get next month events
  const nextMonthEvents = events.filter(e => e.isNextMonth);

  return {
    events,
    currentMonthEvents,
    nextMonthEvents,
    isLoading,
    error,
    refetch: fetchPerformances,
    currentMonthName: getMonthName(new Date()),
    nextMonthName: getMonthName(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)),
  };
};

/**
 * Format date for display
 * @param {Date} date 
 * @returns {string}
 */
const formatEventDate = (date) => {
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

/**
 * Format time for display
 * @param {Date} date 
 * @returns {string}
 */
const formatEventTime = (date) => {
  return date.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Get day of week
 * @param {Date} date 
 * @returns {string}
 */
const getDayOfWeek = (date) => {
  return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
};

/**
 * Get month name
 * @param {Date} date 
 * @returns {string}
 */
const getMonthName = (date) => {
  return date.toLocaleDateString('en-US', { month: 'long' });
};

export default usePerformanceEvents;
